import {useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useAuth} from '~/store/hooks';
import {authActions, authSelectors} from '~/store/slices';

const PIN_VERIFICATION_TIMEOUT = 15 * 1000; // 15 seconds

export const usePinProtectedAction = () => {
  const dispatch = useDispatch();
  const {onSetShowPinConfirm} = useAuth();
  const lastPinVerification = useSelector(authSelectors.getLastPinVerification);
  const pendingAction = useSelector(authSelectors.getPendingAction);

  const pendingCallbackRef = useRef<{type: string, action: () => Promise<void>} | null>(null);

  const lastPinVerificationRef = useRef<number | null>(null);
  lastPinVerificationRef.current = lastPinVerification;
  
  const canExecuteNow = (lastPinVerification: number | null) => {
    if (!lastPinVerification) return false;
    const now = Date.now();
    const timeSinceLastVerification = now - lastPinVerification;
    return timeSinceLastVerification < PIN_VERIFICATION_TIMEOUT;
  };

  const clearAction = useCallback(() => {
    pendingCallbackRef.current = null;
    dispatch(authActions.clearPendingAction());
  }, [dispatch]);

  const executeWithPinProtection = useCallback(
    (actionType: string, action: () => Promise<void>) => {
      // execute straight away if we can do so
      if (canExecuteNow(lastPinVerificationRef.current)) {
        action().catch()
        return;
      }

      // otherwise, store the action, and show the Pin confirmation
      pendingCallbackRef.current = {
        type: actionType,
        action,
      };

      dispatch(authActions.addPendingAction({type: actionType as any}));
      onSetShowPinConfirm({showPinConfirm: true});
    },
    [dispatch, onSetShowPinConfirm],
  );

  // once pin is confirmed, run the action, if it's still the current pending action
  useEffect(() => {
    if (pendingAction && pendingCallbackRef.current && pendingCallbackRef.current.type === pendingAction.type) {
      if (canExecuteNow(lastPinVerification)) {
        pendingCallbackRef.current.action().catch()
        clearAction();
      }
    }
  }, [lastPinVerification, pendingAction, dispatch, clearAction]);

  return {executeWithPinProtection};
}; 